import type { apiDto } from '@rs/contracts'

/**
 * ══ CỬA GỌI REST DÙNG CHUNG (KIEN-TRUC §12) ═══════════════════════════════════════════════════
 * Trước đợt N2, sáu tệp `*-api.ts` mỗi tệp một bản `call` GIỐNG HỆT nhau. Sáu bản sao của cùng một
 * lớp xử lý lỗi là sáu chỗ phải nhớ sửa khi khuôn thân lỗi đổi — mà khuôn đó vừa đổi ở đợt N0.
 */

/** Lỗi máy chủ đã có câu chữ tiếng Việt (§10) — dùng nguyên, KHÔNG viết lại ở màn. */
export class ApiError extends Error {
  readonly status: number
  readonly currentVersion: number | null
  /** Mã máy đọc, ổn định (`forbidden`, `invalid_state`, `version_conflict`…). Xem §10.2. */
  readonly code: string | null

  // ⚠ KHÔNG dùng "parameter properties" — xem `@rs/contracts/optimistic.ts`.
  constructor(status: number, message: string, currentVersion: number | null, code: string | null) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.currentVersion = currentVersion
    this.code = code
  }
}

/**
 * ⭐ HẾT PHIÊN GIỮA CHỪNG (L18, soát 16/10). Máy chủ chỉ trả 401 cho đúng một nghĩa — "chưa đăng
 * nhập" (`plugins/authorize.ts:151`). Trước đây câu đó chỉ hiện trong băng đỏ của hộp đang mở (thường
 * bị lớp phủ che), người dùng bấm Lưu mãi không thấy gì, chuyển màn mới biết đã bị đăng xuất. Bản cũ
 * thì báo rõ rồi đưa về màn đăng nhập (`proship-trans/src/lib/auth.tsx:103-110`). Ở đây `call` phát
 * MỘT sự kiện toàn cục; `AuthProvider` nghe và đưa về màn đăng nhập kèm câu "phiên đã hết hạn".
 */
export const SESSION_EXPIRED_EVENT = 'rs:session-expired'

export const call = async <T>(url: string, init?: RequestInit): Promise<T> => {
  const r = await fetch(url, {
    ...init,
    headers: init?.body === undefined ? {} : { 'content-type': 'application/json' },
  })
  // 204 KHÔNG có thân — gọi `r.json()` là ném. Trả `undefined` cho lời gọi khai `Promise<void>`.
  if (r.ok) return (r.status === 204 ? undefined : await r.json()) as T
  if (r.status === 401 && typeof window !== 'undefined') window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT))
  // Máy chủ luôn trả JSON `{ code, message }`; nếu không thì hiện mã trạng thái thay vì nuốt im lặng.
  const body = await r.json().catch(() => null) as
    { message?: string; currentVersion?: number; code?: string; requestId?: string } | null
  /* ⚠ Lỗi 500 nói "Ghi lại mã yêu cầu bên dưới rồi báo quản trị" và máy chủ CÓ gửi `requestId` — trước đây
     cửa này bỏ rơi nó, nên băng đỏ hứa một mã không hề hiện (soát 16/10, T51). Nối mã vào cuối câu để MỌI
     màn hiện lỗi theo `messageOf` đều mang nó, không phải sửa từng màn. */
  const message = body?.message ?? `Máy chủ trả mã ${r.status}.`
  throw new ApiError(
    r.status,
    typeof body?.requestId === 'string' && body.requestId !== '' ? `${message} ${body.requestId}` : message,
    body?.currentVersion ?? null,
    body?.code ?? null,
  )
}

/**
 * ══ LẤY ĐÚNG MỘT TRANG (cửa sổ hai chiều) ═════════════════════════════════════════════════════
 *
 * Thanh phân trang bản cũ có bốn nút « ‹ › », và cả bốn đi được bằng keyset — xem
 * `@rs/db/keyset.planWindow`. Màn hình KHÔNG tự dựng chuỗi truy vấn phân trang: gom về đây thì
 * tên tham số (`limit` · `cursor` · `dir`) chỉ có một chỗ phải khớp với lược đồ `PageQuery`.
 *
 * ⚠ `cursor` rỗng thì KHÔNG gửi khoá đó lên. Lược đồ khai `z.string().min(1)`, nên gửi chuỗi rỗng
 *   là 400 — một trang đầu hợp lệ biến thành lỗi đỏ trên màn.
 */
export interface PageReq {
  limit: number
  cursor: string | null
  dir: 'next' | 'prev' | 'last'
}

export const fetchPage = <T>(url: string, req: PageReq): Promise<apiDto.PageOf<T>> => {
  const p = new URLSearchParams({ limit: String(req.limit), dir: req.dir })
  if (req.cursor !== null && req.cursor !== '') p.set('cursor', req.cursor)
  const sep = url.includes('?') ? '&' : '?'
  return call<apiDto.PageOf<T>>(`${url}${sep}${p.toString()}`)
}

/**
 * ══ LẤY ĐỦ MỌI TRANG (giải pháp TẠM, có chủ đích) ═════════════════════════════════════════════
 *
 * Từ đợt N2, mọi cửa danh sách của máy chủ đều PHÂN TRANG (trần cứng 200 dòng mỗi lần). Màn hình
 * hiện tại thì vẫn lọc và sắp xếp phía trình duyệt trên toàn bộ danh sách — đổi cách hiển thị là
 * một lần thiết kế lại giao diện, thuộc đợt N5 (TanStack Query + truy vấn vô hạn), không thuộc đợt
 * này. Hàm này đi hết các trang rồi nối lại, nên **hành vi màn hình không đổi một chút nào**. Cái
 * đổi nằm ở máy chủ: một request không còn bốc được cả bảng nữa.
 *
 * ⚠ CÓ TRẦN CỨNG. "Đi cho tới khi hết" mà không có trần thì một lỗi phía máy chủ (con trỏ không
 *   tiến) biến thành vòng lặp vô hạn treo hẳn tab trình duyệt — hỏng nặng hơn thứ nó định làm.
 *   Chạm trần thì DỪNG và ném lỗi có câu chữ, KHÔNG lặng lẽ trả về danh sách thiếu: một danh mục
 *   kế toán hiện thiếu dòng mà không báo gì là đúng kiểu lỗi tệ nhất.
 */
const MAX_PAGES = 100

export const fetchAllPages = async <T>(url: string): Promise<T[]> => {
  const out: T[] = []
  let cursor: string | null = null
  for (let i = 0; i < MAX_PAGES; i += 1) {
    const sep = url.includes('?') ? '&' : '?'
    const page: apiDto.PageOf<T> = await call<apiDto.PageOf<T>>(
      cursor === null ? url : `${url}${sep}cursor=${encodeURIComponent(cursor)}`)
    out.push(...page.rows)
    if (page.nextCursor === null) return out
    cursor = page.nextCursor
  }
  throw tooManyPages()
}

const tooManyPages = (): ApiError => new ApiError(
  0,
  `Danh sách quá ${String(MAX_PAGES)} trang — hãy thu hẹp bộ lọc.`,
  null,
  'too_many_pages',
)

/**
 * Như `fetchAllPages`, nhưng giữ thêm các trường ngoài `rows` của TRANG ĐẦU (vd `lockedTo` của nhật ký dầu, `canApprove` của
 * san dầu) — những trường đó nói về cả danh sách, trang nào cũng như nhau.
 */
export const fetchAllPagesWith = async <T, M extends object>(url: string): Promise<M & { rows: T[] }> => {
  const out: T[] = []
  let first: (M & apiDto.PageOf<T>) | null = null
  let cursor: string | null = null
  for (let i = 0; i < MAX_PAGES; i += 1) {
    const sep = url.includes('?') ? '&' : '?'
    const page: M & apiDto.PageOf<T> = await call<M & apiDto.PageOf<T>>(
      cursor === null ? url : `${url}${sep}cursor=${encodeURIComponent(cursor)}`)
    first ??= page
    out.push(...page.rows)
    if (page.nextCursor === null) return { ...first, rows: out }
    cursor = page.nextCursor
  }
  throw tooManyPages()
}
